/**
 * «Surtido» — the pure part: figures read from the contract's decimal strings, a peer group's
 * identity and the words that name it, and the matrix grid's rows and columns.
 *
 * Decimals arrive as strings (`"12.5"`); a figure the platform does not state is `null`, never 0
 * (ADR 9061). A band is named by its range on its measure, never by its index (platform #940 §9, R3).
 */

import type { BandView, BasisOfferView, LineView, MatrixCellView, PlacementView, PlanView, StoreNodeView } from '@/api/generated';

import { groupValue, type Measure, type PeerGroup } from './scope';

/** A decimal string as a number, or `null` for an absent or unreadable one. */
export function decimal(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** The platform's peer-basis codes. */
export const BASIS_VOLUME_BAND = 'volume_band';
export const BASIS_CLUSTER = 'cluster';
export const BASIS_CLUSTER_AND_VOLUME_BAND = 'cluster_and_volume_band';
export const BASIS_AREA_BAND = 'area_band';

/** The measures a band is cut on (`bandMeasure` of a matrix cell, `measure` of a band). */
export const MEASURE_PAIRS_SOLD = 'pairs_sold';
export const MEASURE_SELLING_AREA = 'selling_area';

/** The cluster scheme status the platform stamps on every output built on a scheme nobody accepted. */
export const SCHEME_PROPOSED = 'proposed';

/** A basis that groups stores by cluster. */
export function usesClusters(basis: string): boolean {
  return basis === BASIS_CLUSTER || basis === BASIS_CLUSTER_AND_VOLUME_BAND;
}

/** A basis that groups stores by a band. */
export function usesBands(basis: string): boolean {
  return basis === BASIS_VOLUME_BAND || basis === BASIS_CLUSTER_AND_VOLUME_BAND || basis === BASIS_AREA_BAND;
}

/**
 * Whether the cluster basis, as offered, stands on a scheme nobody has accepted: the offer lists its schemes (`SchemeOfferView`) with
 * their status, `accepted` only with an acceptance recorded. With no scheme listed nothing says one is accepted, so the label stays —
 * the owner's ruling is to label until the client accepts.
 */
export function offersProposedScheme(offer: Pick<BasisOfferView, 'schemes'>): boolean {
  const schemes = offer.schemes ?? [];
  return schemes.length === 0 || schemes.some((scheme) => scheme.status === SCHEME_PROPOSED);
}

/** Whether a plan or a row stands on a cluster scheme nobody has accepted. */
export function onProposedScheme(schemeStatus: string | null | undefined): boolean {
  return schemeStatus === SCHEME_PROPOSED;
}

/** A plan whose rows are written and may be read. */
export function isCompleted(plan: Pick<PlanView, 'state'>): boolean {
  return plan.state === 'completed';
}

/** A plan still being computed: the page asks again. */
export function isInFlight(plan: Pick<PlanView, 'state'>): boolean {
  return plan.state === 'queued' || plan.state === 'running';
}

/** The newest completed plan of a list — the page's default (`./frame.tsx`). Pure. */
export function newestCompleted(plans: readonly PlanView[]): PlanView | null {
  const done = plans.filter(isCompleted);
  if (done.length === 0) return null;
  return [...done].sort((a, b) => (b.completedAt ?? b.requestedAt).localeCompare(a.completedAt ?? a.requestedAt))[0] ?? null;
}

/** The peer group of a matrix cell, a store × category row or a line. */
export function groupOf(row: { readonly bandIndex?: number | null; readonly clusterId?: string | null }): PeerGroup {
  return { bandIndex: row.bandIndex ?? null, clusterId: row.clusterId ?? null };
}

export function sameGroup(a: PeerGroup, b: PeerGroup): boolean {
  return a.bandIndex === b.bandIndex && a.clusterId === b.clusterId;
}

// ── the matrix grid ──────────────────────────────────────────────────────────

/** One column of the grid: a peer group, with the band's range and the cluster's scheme status. */
export interface GridColumn {
  readonly key: string;
  readonly group: PeerGroup;
  readonly bandLow: number | null;
  readonly bandHigh: number | null;
  readonly departmentFamily: string | null;
  /** What the band was cut on (`bandMeasure` of the cell); `null` where the group has no band. */
  readonly bandMeasure: string | null;
  readonly schemeStatus: string | null;
  /** Stores of the group, as the first cell of the column states them. */
  readonly stores: number;
}

/** One row of the grid: a category and its cell in each column. */
export interface GridRow {
  readonly nodeId: string;
  readonly nodeLabel: string | null;
  readonly cells: ReadonlyMap<string, MatrixCellView>;
}

export interface Grid {
  readonly columns: readonly GridColumn[];
  readonly rows: readonly GridRow[];
}

/** Columns in the order of cluster then band, rows in the order the platform sent. Pure. */
export function toGrid(cells: readonly MatrixCellView[]): Grid {
  const columns = new Map<string, GridColumn>();
  const rows = new Map<string, { nodeId: string; nodeLabel: string | null; cells: Map<string, MatrixCellView> }>();
  for (const cell of cells) {
    const group = groupOf(cell);
    const key = groupValue(group);
    if (!columns.has(key)) {
      columns.set(key, {
        key, group, bandLow: decimal(cell.bandLow), bandHigh: decimal(cell.bandHigh), departmentFamily: cell.departmentFamily ?? null,
        bandMeasure: cell.bandMeasure ?? null, schemeStatus: cell.schemeStatus ?? null, stores: cell.storesInGroup,
      });
    }
    let row = rows.get(cell.nodeId);
    if (row === undefined) {
      row = { nodeId: cell.nodeId, nodeLabel: cell.nodeLabel ?? null, cells: new Map() };
      rows.set(cell.nodeId, row);
    }
    row.cells.set(key, cell);
  }
  const ordered = [...columns.values()].sort((a, b) => (a.group.clusterId ?? '').localeCompare(b.group.clusterId ?? '') || (a.group.bandIndex ?? -1) - (b.group.bandIndex ?? -1));
  return { columns: ordered, rows: [...rows.values()] };
}

/** What a matrix cell says for one measure. `null`: the platform states none. */
export function cellMeasure(cell: MatrixCellView, measure: Measure): number | null {
  switch (measure) {
    case 'models': return decimal(cell.medianBreadth);
    case 'salesPerModel': return decimal(cell.medianSalesPerOption);
    case 'sales': return decimal(cell.salesValue);
    case 'units': return decimal(cell.unitsSold);
    case 'margin': return decimal(cell.marginValue);
    case 'carrying': return cell.storesInGroup === 0 ? null : cell.storesCarrying / cell.storesInGroup;
    // A cell with no baseline judges none of the flags: the platform sends null for those counts, and null is not nought (ADR 9061).
    case 'under': return cell.underStores ?? null;
    case 'over': return cell.overStores ?? null;
    case 'missing': return cell.missingStores ?? null;
    case 'tail': return cell.tailOptions ?? null;
    case 'addNow': return cell.addedNow ?? null;
    case 'remove': return cell.removed ?? null;
  }
}

/** How a measure is written: money in the plan's currency, a share, or a plain count. */
export type MeasureForm = 'money' | 'share' | 'count' | 'decimal';

export const MEASURE_FORM: Readonly<Record<Measure, MeasureForm>> = {
  models: 'decimal', salesPerModel: 'money', sales: 'money', units: 'count', margin: 'money', carrying: 'share',
  under: 'count', over: 'count', missing: 'count', tail: 'count', addNow: 'count', remove: 'count',
};

/**
 * The shade (0–1) of a cell among the cells of the same measure: its share of the largest figure. A shade
 * is a second cue only: every cell writes its figure, so the grid never relies on colour alone (§12.6).
 */
export function shade(value: number | null, largest: number): number {
  if (value === null || largest <= 0) return 0;
  return Math.max(0, Math.min(1, value / largest));
}

// ── rows of a plan ───────────────────────────────────────────────────────────

/** The flags a store × category row carries, in the order the page lists them. */
export function flagsOf(row: StoreNodeView): readonly string[] {
  const flags: string[] = [];
  if (isRefused(row)) flags.push('refused');
  if (row.underAssorted) flags.push('under_assorted');
  if (row.overAssorted) flags.push('over_assorted');
  if (row.missing) flags.push('missing');
  if (row.bounded) flags.push('bounded');
  return flags;
}

/** Whether the row is one the plan did not judge for this basis: it keeps its row and says why. */
export function isRefused(row: StoreNodeView): boolean {
  return row.refusal !== null && row.refusal !== undefined;
}

/** Whether a row's target is held by the store's own room: the peers' figure against the limit. */
export function isBounded(row: StoreNodeView): boolean {
  return row.bounded === true && row.room !== null && row.room !== undefined;
}

/**
 * Invariant 1 of the plan, written back: today + added now − removed + gap = target. `true` when the row's
 * figures add up; a failure is a defect to show, never to hide.
 */
export function rowAddsUp(row: StoreNodeView): boolean {
  return isRefused(row) || row.breadth + row.addedNow - row.removed + row.gap === row.target;
}

// ── lines ────────────────────────────────────────────────────────────────────

/** Who sizes the quantity of an add-now line. */
export type QuantityOwner = 'initial_allocation' | 'nobody';

/** `initial_allocation` when it owns the quantity; `nobody` for an add-now line nobody sizes in v1; `null` for other actions. */
export function quantityOwnerOf(line: Pick<LineView, 'action' | 'quantityOwner'>): QuantityOwner | null {
  if (line.action !== 'add_now') return null;
  return line.quantityOwner === 'initial_allocation' ? 'initial_allocation' : 'nobody';
}

/** A band's range as numbers: both ends, or one open end. */
export function bandRange(band: Pick<BandView, 'low' | 'high'>): { readonly low: number | null; readonly high: number | null } {
  return { low: decimal(band.low), high: decimal(band.high) };
}

/** A placement that has a group (`placed`) or the reason it has none. */
export function isPlaced(placement: PlacementView): boolean {
  return placement.state === 'placed';
}
